import { NOTE_ENTITY_TYPES, validNoteEntityContext, type NoteEntityContext, type QuickNoteFileSource } from './noteEntityContext';

export type QuickNoteDraft = {
  version: 1; ownerId: number; id: string; updatedAt: string; text: string;
  pendingSave?: { clientKey: string; text: string; context: NoteEntityContext | null; destination?: 'record' | 'notebook'; conflict?: boolean } | null;
  notebookMode?: boolean;
  context: NoteEntityContext | null; entityType: NoteEntityContext['entityType']; sourceFile: QuickNoteFileSource | null;
  travelMode: boolean; walletId: number | null; travelKind: 'note' | 'todo'; travelItemId: number | null;
};
type StorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;
const memory = new Map<number, QuickNoteDraft>();
const observed = new Map<number, string | null>();
const dirty = new Set<number>();
export const quickNoteDraftKey = (ownerId: number) => `experviser:quick-note-draft:v1:${ownerId}`;
const positiveId = (id: unknown): id is number => typeof id === 'number' && Number.isSafeInteger(id) && id > 0;

/** The backend-verified cache supplies identity. JWT claims only reject a stale cache under another session. */
export function quickNoteDraftOwner(payload: any, token: string | null, now = Date.now()): number | null {
  const id = payload?.user?.id ?? payload?.id;
  if (!positiveId(id) || !token) return null;
  try {
    const claims = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return Number(claims.id) === id && typeof claims.exp === 'number' && claims.exp * 1000 > now ? id : null;
  } catch { return null; }
}

function validDraft(value: any, ownerId: number): value is QuickNoteDraft {
  if (!value || value.version !== 1 || value.ownerId !== ownerId || typeof value.id !== 'string' || !value.id
    || typeof value.text !== 'string' || !Number.isFinite(Date.parse(value.updatedAt))
    || !NOTE_ENTITY_TYPES.includes(value.entityType) || typeof value.travelMode !== 'boolean'
    || !['note', 'todo'].includes(value.travelKind)
    || (value.walletId !== null && !positiveId(value.walletId))
    || (value.travelItemId !== null && !positiveId(value.travelItemId))) return false;
  if (value.notebookMode !== undefined && typeof value.notebookMode !== 'boolean') return false;
  if (value.pendingSave?.destination !== undefined && !['record', 'notebook'].includes(value.pendingSave.destination)) return false;
  if (value.pendingSave?.destination === 'notebook' && (!value.notebookMode || value.travelMode || value.context !== null)) return false;
  if (value.pendingSave && (typeof value.pendingSave.clientKey !== 'string' || typeof value.pendingSave.text !== 'string'
    || (value.pendingSave.destination === 'notebook' ? value.pendingSave.context !== null : !validNoteEntityContext(value.pendingSave.context)))) return false;
  if (value.context !== null && !validNoteEntityContext(value.context)) return false;
  const file = value.sourceFile;
  return file === null || (positiveId(file?.id) && typeof file.name === 'string' && Array.isArray(file.targets)
    && file.targets.every((target: any) => validNoteEntityContext(target) && typeof target.label === 'string'));
}

export function readQuickNoteDraft(ownerId: number, storage: StorageLike): { draft: QuickNoteDraft | null; error: boolean } {
  const retained = memory.get(ownerId);
  try {
    const raw = storage.getItem(quickNoteDraftKey(ownerId));
    if (dirty.has(ownerId) && retained) return { draft: retained, error: true };
    observed.set(ownerId, raw);
    if (raw === null) { memory.delete(ownerId); return { draft: null, error: false }; }
    const parsed = JSON.parse(raw);
    if (!validDraft(parsed, ownerId)) return { draft: null, error: true };
    memory.set(ownerId, parsed);
    return { draft: parsed, error: false };
  } catch { return { draft: retained ?? null, error: true }; }
}

export function writeQuickNoteDraft(draft: QuickNoteDraft, storage: StorageLike): boolean {
  memory.set(draft.ownerId, draft); dirty.add(draft.ownerId);
  try {
    // Do not overwrite another tab's newer draft, deletion or confirmed save.
    const raw = storage.getItem(quickNoteDraftKey(draft.ownerId));
    if (raw !== (observed.get(draft.ownerId) ?? null)) return false;
    const next = JSON.stringify(draft);
    storage.setItem(quickNoteDraftKey(draft.ownerId), next);
    observed.set(draft.ownerId, next); dirty.delete(draft.ownerId); return true;
  } catch { return false; }
}

/** Only explicit discard or a confirmed CRM save may clear this exact device version. */
export function removeQuickNoteDraft(ownerId: number, storage: StorageLike): boolean {
  try {
    if (storage.getItem(quickNoteDraftKey(ownerId)) !== (observed.get(ownerId) ?? null)) return false;
    storage.removeItem(quickNoteDraftKey(ownerId)); memory.delete(ownerId); dirty.delete(ownerId); observed.set(ownerId, null); return true;
  } catch { return false; }
}
